import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {AgentInfo} from '../types/session'
import {isCLIAgentSlug} from '../utils/cli'
import {COPILOT_CLI_SLUG, COPILOT_VSCODE_SLUG} from '../utils/agent-helpers'

export function generateAgentInfoQueryKey(agentId: number | undefined | null, isCliAgent: boolean, agentSlug?: string) {
  return ['agent-info', agentId, isCliAgent, agentSlug]
}

// Hardcoded info for CLI-like sessions since they don't have agent metadata in the monolith
const CLI_AGENT_INFO: AgentInfo = {
  display_name: 'Copilot CLI',
  slug: COPILOT_CLI_SLUG,
  avatar_url: '',
  doc_url: '',
}

const VSCODE_AGENT_INFO: AgentInfo = {
  display_name: 'Copilot in VS Code',
  slug: COPILOT_VSCODE_SLUG,
  avatar_url: '',
  doc_url: '',
}

const CLI_LIKE_AGENT_INFO: Record<string, AgentInfo> = {
  [COPILOT_CLI_SLUG]: CLI_AGENT_INFO,
  [COPILOT_VSCODE_SLUG]: VSCODE_AGENT_INFO,
}

/**
 *
 * This hook fetches agent metadata (name, avatar, etc.) from the monolith
 * for agents that are returned from CAPI sessions but don't include
 * full agent information.
 *
 * For CLI-like sessions (CLI, VS Code), returns hardcoded info since these
 * agents aren't registered in the monolith's agent registry.
 */
export function useAgentInfo(agentId: number | undefined | null, agentSlug?: string) {
  const isCliAgent = isCLIAgentSlug(agentSlug)

  return useQuery<AgentInfo | null>({
    queryKey: generateAgentInfoQueryKey(agentId, isCliAgent, agentSlug),
    queryFn: async () => {
      // Return hardcoded info for CLI-like agents
      if (isCliAgent && agentSlug) {
        return CLI_LIKE_AGENT_INFO[agentSlug] ?? null
      }

      const response = await reactFetchJSON(`/copilot/agent-sessions/agents/${agentId}`)

      if (!response.ok) {
        // For server errors (5xx), throw to allow retries since they're transient.
        if (response.status >= 500) {
          throw new Error(`Failed to fetch agent info for agent ${agentId}: ${response.status}`)
        }
        // For client errors (e.g. 404), return null to prevent infinite refetch loops.
        // TanStack Query v5 treats queries with data === undefined as always stale,
        // so throwing would cause repeated refetches regardless of staleTime.
        return null
      }

      const data = await response.json()
      return data.agent_info
    },
    enabled: !!agentId,
    staleTime: Infinity,
    retry: 3,
  })
}
